/* ============================================================
   FLEETTRACK TELEMATICS — Header JS
   assets/ft-header.js   (loaded with defer)

   Modules — all pure functions, IIFE-scoped, ES5 compatible:
     1. initAnnouncement  — auto-cycle slides, dismiss, session persist
     2. updateMegaTop     — reads bar.getBoundingClientRect().bottom
     3. initScrollShadow  — is-scrolled class on scroll
     4. initMegaMenus     — hover/click/keyboard, backdrop
     5. initLoginDropdown — open/close/outside-click
     6. initMobileDrawer  — open/close/focus-trap/swipe, z-index >= 9100
     7. initAccordion     — mobile drawer accordion
     8. initSearch        — search overlay open/close
     9. initThemeEditor   — shopify:section:load hot-reload
   ============================================================ */

(function () {
  'use strict';

  /* ── Shared utilities ───────────────────────────────────── */
  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) {
    if (el) el.addEventListener(ev, fn, opts || false);
  }

  var LOCK_CLASS   = 'fth-locked';
  var ANN_KEY      = 'ft-ann-dismissed';
  var MEGA_DELAY   = 160;   /* ms grace period before mega closes */
  var hoverTimer   = null;
  var activeMegaId = null;

  /* ════════════════════════════════════════════════════════
     1. ANNOUNCEMENT BAR
     NOTE: First slide already has is-active in HTML so text
     is visible even before this JS executes (no flash).
     JS only adds cycling and dismiss behaviour.
  ════════════════════════════════════════════════════════ */
  function initAnnouncement() {
    var ann = qs('[data-fth-ann]');
    var bar = qs('[data-fth-bar]');
    if (!ann) return;

    var slides     = qsa('[data-fth-ann-slide]', ann);
    var btnDismiss = qs('[data-fth-ann-dismiss]', ann);
    var current    = 0;
    var timer      = null;

    /* Utility-only bar (no announcement messages): always visible, never dismissible */
    if (slides.length === 0) return;

    /* Restore dismissed state from previous session (only when there are messages) */
    if (sessionStorage.getItem(ANN_KEY)) {
      ann.classList.add('is-hidden');
      if (bar) bar.classList.add('ann-gone');
      document.documentElement.style.setProperty('--fth-ann-h', '0px');
      updateMegaTop();
      return;
    }

    /* Ensure first slide is active (HTML already sets it, this is belt+braces) */
    slides.forEach(function (s, i) {
      s.classList.toggle('is-active', i === 0);
    });

    /* Auto-cycle when multiple slides */
    if (slides.length > 1) {
      function cycle() {
        slides[current].classList.remove('is-active');
        current = (current + 1) % slides.length;
        slides[current].classList.add('is-active');
      }
      timer = setInterval(cycle, 4500);
      on(ann, 'mouseenter', function () { clearInterval(timer); });
      on(ann, 'mouseleave', function () { timer = setInterval(cycle, 4500); });
    }

    /* Dismiss */
    on(btnDismiss, 'click', function () {
      clearInterval(timer);
      ann.classList.add('is-hidden');
      if (bar) bar.classList.add('ann-gone');
      document.documentElement.style.setProperty('--fth-ann-h', '0px');
      sessionStorage.setItem(ANN_KEY, '1');
      updateMegaTop();
    });
  }

  /* ════════════════════════════════════════════════════════
     2. MEGA TOP POSITION
     Reads actual bar bottom from viewport — correct whether
     scrolled or not, and whether ann bar is dismissed or not.
  ════════════════════════════════════════════════════════ */
  function updateMegaTop() {
    var bar = qs('[data-fth-bar]');
    if (!bar) return;
    var bottom = bar.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--fth-mega-top', bottom + 'px');
  }

  /* ════════════════════════════════════════════════════════
     2b. DYNAMIC ANN HEIGHT
     Reads actual announcement bar height so --fth-ann-h is
     never hardcoded. Header top position + spacer height
     both update automatically.
  ════════════════════════════════════════════════════════ */
  function updateAnnHeight() {
    var ann = qs('[data-fth-ann]');
    var h = (ann && !ann.classList.contains('is-hidden')) ? ann.offsetHeight : 0;
    document.documentElement.style.setProperty('--fth-ann-h', h + 'px');
    updateMegaTop();
  }

  /* ════════════════════════════════════════════════════════
     3. SCROLL SHADOW
  ════════════════════════════════════════════════════════ */
  function initScrollShadow() {
    var bar = qs('[data-fth-bar]');
    if (!bar) return;
    function onScroll() {
      var y = window.scrollY;
      bar.classList.toggle('is-scrolled', y > 8);
      /* After scrolling, collapse the utility + announcement strips and pin the nav bar */
      document.documentElement.classList.toggle('fth-scrolled', y > 40);
      updateMegaTop();
    }
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ════════════════════════════════════════════════════════
     4. MEGA MENUS
  ════════════════════════════════════════════════════════ */
  function initMegaMenus() {
    var backdrop = qs('[data-fth-mega-backdrop]');
    var navItems = qsa('.fth-nav__item[data-mega-id]');

    function getMega(id)    { return qs('[data-fth-mega="' + id + '"]'); }
    function getNavItem(id) { return qs('.fth-nav__item[data-mega-id="' + id + '"]'); }
    function getTrigger(id) {
      var ni = getNavItem(id);
      return ni ? qs('[data-fth-mega-trigger]', ni) : null;
    }

    function openMega(id) {
      if (activeMegaId === id) return;
      if (activeMegaId) closeMega();
      var mega = getMega(id);
      if (!mega) return;
      updateMegaTop();
      mega.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      var ni = getNavItem(id);
      if (ni) ni.classList.add('is-active');
      var tr = getTrigger(id);
      if (tr) tr.setAttribute('aria-expanded', 'true');
      activeMegaId = id;
    }

    function closeMega() {
      if (!activeMegaId) return;
      var mega = getMega(activeMegaId);
      var ni   = getNavItem(activeMegaId);
      var tr   = getTrigger(activeMegaId);
      if (mega) mega.classList.remove('is-open');
      if (ni)   ni.classList.remove('is-active');
      if (tr)   tr.setAttribute('aria-expanded', 'false');
      if (backdrop) backdrop.classList.remove('is-open');
      activeMegaId = null;
    }

    navItems.forEach(function (item) {
      var id      = item.dataset.megaId;
      var trigger = qs('[data-fth-mega-trigger]', item);

      on(item, 'mouseenter', function () { clearTimeout(hoverTimer); openMega(id); });
      on(item, 'mouseleave', function () { hoverTimer = setTimeout(closeMega, MEGA_DELAY); });

      on(trigger, 'click', function (e) {
        e.preventDefault();
        activeMegaId === id ? closeMega() : openMega(id);
      });

      on(trigger, 'keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); trigger.click(); }
        if (e.key === 'Escape') { closeMega(); item.focus(); }
      });
    });

    qsa('[data-fth-mega]').forEach(function (mega) {
      on(mega, 'mouseenter', function () { clearTimeout(hoverTimer); });
      on(mega, 'mouseleave', function () { hoverTimer = setTimeout(closeMega, MEGA_DELAY); });
    });

    on(backdrop, 'click', closeMega);
    on(document, 'keydown', function (e) { if (e.key === 'Escape') closeMega(); });
    on(window, 'scroll', function () { if (activeMegaId) closeMega(); }, { passive: true });
  }

  /* ════════════════════════════════════════════════════════
     5. LOGIN DROPDOWN
  ════════════════════════════════════════════════════════ */
  function initLoginDropdown() {
    var trigger  = qs('[data-fth-login-trigger]');
    var dropdown = qs('[data-fth-login-dropdown]');
    if (!trigger || !dropdown) return;

    function close() {
      trigger.classList.remove('is-open');
      dropdown.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
    }

    on(trigger, 'click', function (e) {
      e.stopPropagation();
      var open = dropdown.classList.contains('is-open');
      close();
      if (!open) {
        trigger.classList.add('is-open');
        dropdown.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
    });

    on(document, 'click',   function (e) { if (!trigger.contains(e.target)) close(); });
    on(document, 'keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ════════════════════════════════════════════════════════
     6. MOBILE DRAWER
     z-index: 9110 (panel) / 9100 (overlay) — both above the
     fixed header at 9000. This was the root cause of the
     "clicking does nothing" bug (drawer was z:500, hidden
     behind the header).
  ════════════════════════════════════════════════════════ */
  function initMobileDrawer() {
    var overlay  = qs('[data-fth-drawer-overlay]');
    var drawer   = qs('[data-fth-drawer]');
    var btnOpen  = qs('[data-fth-drawer-open]');
    var btnClose = qs('[data-fth-drawer-close]');
    if (!drawer) return;

    function openDrawer() {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      if (btnOpen) {
        btnOpen.classList.add('is-open');
        btnOpen.setAttribute('aria-expanded', 'true');
      }
      document.body.classList.add(LOCK_CLASS);
      /* Focus first focusable element inside drawer */
      setTimeout(function () {
        var first = qs('button, a[href]', drawer);
        if (first) first.focus();
      }, 320);
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      if (btnOpen) {
        btnOpen.classList.remove('is-open');
        btnOpen.setAttribute('aria-expanded', 'false');
      }
      document.body.classList.remove(LOCK_CLASS);
      if (btnOpen) btnOpen.focus();
    }

    on(btnOpen,  'click', function () { drawer.classList.contains('is-open') ? closeDrawer() : openDrawer(); });
    on(btnClose, 'click', closeDrawer);

    /* Overlay: listen for both click (desktop) AND touchend (iOS Safari).
       iOS Safari does not reliably fire click on non-interactive elements
       even with cursor:pointer — touchend is the safe fallback.          */
    on(overlay, 'click',    closeDrawer);
    on(overlay, 'touchend', function (e) {
      /* Only close if the touch ended ON the overlay (not a swipe) */
      if (e.target === overlay) { e.preventDefault(); closeDrawer(); }
    }, { passive: false });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    /* Swipe left to close */
    var swipeStartX = 0;
    on(drawer, 'touchstart', function (e) { swipeStartX = e.touches[0].clientX; }, { passive: true });
    on(drawer, 'touchend',   function (e) {
      if (swipeStartX - e.changedTouches[0].clientX > 60) closeDrawer();
    }, { passive: true });

    /* Focus trap */
    on(drawer, 'keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusable = qsa('a[href], button:not([disabled])', drawer);
      if (!focusable.length) return;
      var first = focusable[0];
      var last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ════════════════════════════════════════════════════════
     7. MOBILE ACCORDION
  ════════════════════════════════════════════════════════ */
  function initAccordion() {
    qsa('[data-fth-acc-trigger]').forEach(function (trigger) {
      var id    = trigger.dataset.fthAccTrigger;
      var panel = qs('#' + id);
      if (!panel) return;

      on(trigger, 'click', function () {
        var isOpen = panel.classList.contains('is-open');
        /* Close all */
        qsa('.fth-drawer__acc.is-open').forEach(function (p) { p.classList.remove('is-open'); });
        qsa('.fth-drawer__trigger.is-open').forEach(function (t) { t.classList.remove('is-open'); });
        /* Open if was closed */
        if (!isOpen) {
          panel.classList.add('is-open');
          trigger.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        } else {
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /* ════════════════════════════════════════════════════════
     8. SEARCH OVERLAY
  ════════════════════════════════════════════════════════ */
  function initSearch() {
    var overlay  = qs('[data-fth-search-overlay]');
    var input    = qs('[data-fth-search-input]');
    var closeBtns = qsa('[data-fth-search-close]');
    var openBtns  = qsa('[data-fth-search-open]');
    if (!overlay) return;

    function openSearch() {
      overlay.classList.add('is-open');
      document.body.classList.add(LOCK_CLASS);
      setTimeout(function () { if (input) input.focus(); }, 200);
    }
    function closeSearch() {
      overlay.classList.remove('is-open');
      document.body.classList.remove(LOCK_CLASS);
    }

    openBtns.forEach(function (btn) { on(btn, 'click', openSearch); });
    closeBtns.forEach(function (btn) { on(btn, 'click', closeSearch); });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeSearch();
    });
  }

  /* ════════════════════════════════════════════════════════
     9. THEME EDITOR
  ════════════════════════════════════════════════════════ */
  function initThemeEditor() {
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target.classList.contains('ft-header-section')) boot();
    });
    document.addEventListener('shopify:block:select', updateMegaTop);
  }

  /* ── Boot ─────────────────────────────────────────────── */
  function boot() {
    /* Measure actual announcement bar height — sets --fth-ann-h dynamically
       so neither hardcoded values nor layout shifts ever occur */
    updateAnnHeight();
    initAnnouncement();
    updateMegaTop();
    initScrollShadow();
    initMegaMenus();
    initLoginDropdown();
    initMobileDrawer();
    initAccordion();
    initSearch();
  }

  /* Run after DOM is ready (script has defer attribute) */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-measure on resize — handles font size changes, orientation etc. */
  var resizeTimer = null;
  on(window, 'resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { updateAnnHeight(); }, 150);
  }, { passive: true });

  initThemeEditor();

})();